import { useEffect, useMemo, useState } from 'react';
import api from '../../../services';
import { createQuoteDraft, detachQuoteDraft, persistQuoteDraft, quoteRequest, sourceQuote } from './quoteDraft';

export default function useQuoteAttachment({ userId, storyId, location, reactionId, savedQuote }) {
  const request = quoteRequest(location);
  const requestId = request?.id;
  const requestKey = request?.key;
  const draft = useMemo(() => createQuoteDraft(userId, storyId,
    requestKey ? { id: requestId, key: requestKey } : null), [userId, storyId, requestId, requestKey]);
  const [revision, setRevision] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    persistQuoteDraft(draft);
    if (!draft.id || draft.invalid || draft.placementRemoved) return undefined;
    let active = true;
    const id = draft.id;
    setLoaded({ draft, id, pending: true });
    api.reactionLoad({ id }).then(response => {
      const quote = sourceQuote(response, id, storyId);
      if (active) setLoaded({ draft, id, quote, pending: false });
    }).catch(() => {
      if (active) setLoaded({ draft, id, error: true, pending: false });
    });
    return () => { active = false; };
  }, [draft, userId, storyId, revision, attempt]);

  const current = loaded?.draft === draft && loaded.id === draft.id ? loaded : null;
  const ownSource = Boolean(draft.id && reactionId && Number(draft.id) === Number(reactionId));
  const error = Boolean(draft.invalid || ownSource || current?.error);
  const pending = Boolean(draft.id && !error && (!current || current.pending));
  return {
    draft,
    quote: draft.id === undefined && !draft.invalid ? savedQuote : error ? null : current?.quote ?? null,
    pending,
    error,
    changed: draft.id !== undefined,
    setPlacement: present => {
      if (Boolean(draft.placementRemoved) === !present) return;
      draft.placementRemoved = !present;
      persistQuoteDraft(draft);
      setRevision(value => value + 1);
    },
    detach: () => { detachQuoteDraft(draft); setRevision(value => value + 1); },
    retry: () => setAttempt(value => value + 1),
  };
}
